import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, AlertCircle, AlertTriangle, Shield } from "lucide-react";
import { browseMedication } from "../lib/api";
import { cn } from "../lib/utils";

const RISK_TONE = {
  HIGH: "bg-rose-50 text-rose-700 border-rose-200",
  MODERATE: "bg-amber-50 text-amber-800 border-amber-200",
  LOW: "bg-lime-50 text-lime-700 border-lime-200",
};
const RISK_ICON = { HIGH: AlertCircle, MODERATE: AlertTriangle, LOW: Shield };
const RISK_HEADING = {
  HIGH: "Avoid — known to conflict",
  MODERATE: "Use with caution",
  LOW: "Generally OK with care",
};

function GroupBlock({ level, rows, medId }) {
  if (!rows?.length) return null;
  const Icon = RISK_ICON[level];
  return (
    <section
      data-testid={`browse-med-group-${level}`}
      className="mb-8"
    >
      <div className={cn(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12px] font-semibold uppercase tracking-wide mb-4",
        RISK_TONE[level],
      )}>
        <Icon className="w-3.5 h-3.5" />
        {level} — {rows.length} ingredient{rows.length === 1 ? "" : "s"}
      </div>
      <p className="text-sm text-ink-soft mb-4">{RISK_HEADING[level]}</p>
      <ul className="space-y-2">
        {rows.map((r) => {
          const ing = r.ingredient || {};
          return (
            <li key={r.id} className="rounded-xl border border-surface-line bg-white p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <Link
                    to={`/browse/ing/${ing.id}`}
                    data-testid={`browse-med-ingredient-${ing.id}`}
                    className="font-semibold text-ink hover:underline"
                  >
                    {ing.name}
                  </Link>
                  {ing.inci_name && ing.inci_name !== ing.name && (
                    <span className="ml-2 text-[12px] text-ink-mute">({ing.inci_name})</span>
                  )}
                  {r.summary && <p className="mt-1 text-sm text-ink-soft">{r.summary}</p>}
                  {r.recommendation && (
                    <p className="mt-1 text-sm text-ink"><b>What to do:</b> {r.recommendation}</p>
                  )}
                </div>
                <Link
                  to={`/browse/pair/${medId}/${ing.id}`}
                  className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink"
                >
                  Details <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function BrowseMedication() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await browseMedication(id);
        setData(res);
      } catch (e) {
        setErr(e?.response?.status === 404 ? "Not found" : "Could not load");
      }
    })();
  }, [id]);

  if (err) {
    return (
      <main className="max-w-screen-lg mx-auto px-4 sm:px-6 md:px-10 py-16">
        <p className="text-ink-soft">{err}</p>
        <Link to="/browse" className="text-ink underline">← Back to browse</Link>
      </main>
    );
  }
  if (!data) return <main className="max-w-screen-lg mx-auto px-4 sm:px-6 md:px-10 py-16 text-ink-mute">Loading…</main>;

  const m = data.medication;
  return (
    <main data-testid="browse-medication-page" className="max-w-screen-lg mx-auto px-4 sm:px-6 md:px-10 py-10 sm:py-14">
      <button
        data-testid="browse-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[13px] text-ink-mute hover:text-ink mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <header className="mb-8 pb-6 border-b border-surface-line">
        <p className="text-[11px] uppercase tracking-[0.16em] text-ink-mute mb-2">
          Medication
        </p>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-ink">
          {m.name || m.generic_name}
        </h1>
        {m.brand_names?.length > 0 && m.brand_names[0] !== m.name && (
          <p className="mt-2 text-sm text-ink-mute">
            Also sold as: {m.brand_names.join(", ")}
          </p>
        )}
        {m.category && (
          <p className="mt-2 text-sm text-ink-soft">{m.category}</p>
        )}
        {m.description && (
          <p className="mt-4 text-base text-ink-soft max-w-2xl">{m.description}</p>
        )}
        <div
          data-testid="browse-med-counts"
          className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-ink-mute"
        >
          <span><b className="text-ink">{data.counts.HIGH || 0}</b> high risk</span>
          <span><b className="text-ink">{data.counts.MODERATE || 0}</b> moderate</span>
          <span><b className="text-ink">{data.counts.LOW || 0}</b> low</span>
        </div>
      </header>

      <GroupBlock level="HIGH" rows={data.groups.HIGH} medId={id} />
      <GroupBlock level="MODERATE" rows={data.groups.MODERATE} medId={id} />
      <GroupBlock level="LOW" rows={data.groups.LOW} medId={id} />

      {data.total === 0 && (
        <p className="text-ink-mute">No tracked interactions for this medication yet.</p>
      )}
    </main>
  );
}
